const RUNTIME_CONFIG_PATH = '/runtime-config.json';

let runtimeConfig = null;

const requiredKeys = [
  'agentUrl',
  'apiEndpoint',
  'appEnv',
  'authMode',
  'listMonthsUrl',
  'region',
  'resyncUrl',
  'syncStatusUrl',
];

export const validateRuntimeConfig = (config) => {
  const missingKeys = requiredKeys.filter((key) => !config?.[key]);
  const authMode = config?.authMode;

  if (authMode !== 'oauth' && authMode !== 'placeholder') {
    throw new Error(
      `Runtime config has invalid authMode '${authMode}'. Expected 'oauth' or 'placeholder'.`
    );
  }

  const authModeKeys =
    authMode === 'oauth'
      ? ['msClientId', 'msTenantId']
      : ['adminUsername', 'adminPassword'];
  missingKeys.push(
    ...authModeKeys.filter((key) => !config?.[key] && !missingKeys.includes(key))
  );

  if (missingKeys.length > 0) {
    throw new Error(
      `Runtime config is missing required keys: ${missingKeys.join(', ')}`
    );
  }

  return config;
};

/**
 * Optional feature flags arrive as strings, not booleans: the container renders
 * runtime-config.json with `jq --arg`, which always emits a JSON string, while
 * the host-side Python renderer emits a real boolean. Accept both.
 *
 * `fallback` is what an absent or empty key means. It defaults to false, so a
 * missing key never switches a feature on by accident — but a flag whose
 * feature is part of the product rather than an experiment passes `true` and
 * only turns off when something explicitly says so.
 */
export const isFlagEnabled = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = String(value ?? '').trim().toLowerCase();

  if (normalized === '') {
    return fallback;
  }

  if (['true', '1', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['false', '0', 'no', 'off'].includes(normalized)) {
    return false;
  }

  return fallback;
};

export const loadRuntimeConfig = async () => {
  if (runtimeConfig) {
    return runtimeConfig;
  }

  const response = await fetch(RUNTIME_CONFIG_PATH, { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(
      `Failed to load runtime config from ${RUNTIME_CONFIG_PATH}: ${response.status}`
    );
  }

  runtimeConfig = validateRuntimeConfig(await response.json());
  return runtimeConfig;
};

export const getRuntimeConfig = () => {
  if (!runtimeConfig) {
    throw new Error('Runtime config has not been loaded yet.');
  }

  return runtimeConfig;
};
